import React from "react";
import PolicyLayout from "../../../components/PolicyLayout";
import s from "../../../styles/PolicyLayout.module.css";

export default function AiHomeworkPrivacy() {
  return (
    <PolicyLayout
      title="privacy policy"
      appName="Ai Homework Solver"
      date="NOVEMBER 2025"
    >
      <p>
        Developer: Aleksandra Rakitina
        <br />
        Contact (privacy requests): alexwadek@gmail.com
      </p>

      <div className={s.title}>1) Introduction and Scope</div>
      <p>
        This Privacy Policy explains how the mobile application “Ai Homework Solver” (the
        “App”) collects, uses, stores, and discloses information about users and their
        interactions with features that include camera capture, gallery import, OCR text
        extraction, AI-powered step-by-step solutions, PDF export and sharing, local history
        management, notifications, analytics, and subscription billing. The App is designed
        with a local-first approach so core content can remain on your device unless you
        enable sync or share outputs. We do not operate our own backend for chat or solution
        computation; prompts and extracted text are sent directly to service providers over
        HTTPS. By using the App, you acknowledge this Policy and agree to its terms. If you
        disagree, uninstall the App and contact us regarding your data options.
      </p>

      <div className={s.title}>2) Controller and Contact Information</div>
      <p>
        The data controller responsible for decisions about how and why your data is
        processed is Aleksandra Rakitina. You can reach us at alexwadek@gmail.com for
        questions about this Policy or to exercise rights such as access, deletion,
        correction, restriction, objection, and data portability. For security, we verify
        your identity before acting on requests, typically by confirming control of the
        account email or other minimally sufficient information. We keep correspondence
        related to privacy for compliance, security, and documentation. Where a request
        concerns platform-level controls like subscription cancellation or notification
        permissions, we will direct you to the relevant settings. We may refuse or limit
        requests when permitted by law, for example to protect the rights and freedoms of
        other users or to preserve evidence of misuse.
      </p>

      <div className={s.title}>3) Definitions</div>
      <p>
        “Personal Data” means any information relating to an identified or identifiable
        natural person, including identifiers provided by authentication or billing
        platforms. “Processing” includes collection, recording, storage, use, disclosure, and
        deletion. “Service Providers/Processors” are third parties acting on our instructions
        to supply the App’s functionality, notably authentication, OCR, AI inference,
        analytics, push messaging, optional cloud storage, and subscription entitlements.
        “Image Content” refers to photos you capture or import for OCR. “Extracted Text”
        refers to recognized text produced by OCR engines. “Solution Content” is the
        AI-generated explanation and steps. “Technical Data” covers device and app
        information and diagnostic logs. “Pseudonymization” means processing data so it
        cannot be attributed to a specific user without additional information kept
        separately. These definitions help avoid ambiguity and ensure consistent
        interpretation throughout this Policy.
      </p>

      <div className={s.title}>4) Data Minimization and Purpose Limitation</div>
      <p>
        We collect only what is necessary to deliver the features you request: camera access
        to capture tasks, storage access to import or save files, OCR to recognize text, AI
        inference to produce explanations, and billing identifiers to manage subscriptions.
        Where feasible, Image Content and Extracted Text are processed on-device; if you use
        cloud OCR or send text to AI, we transmit only the minimum necessary fields.
        Analytics focus on reliability and aggregated usage patterns rather than profiling.
        Retention is limited to what is needed for functionality, security, and legal
        obligations. Optional features such as Firestore sync or certain attribution metrics
        are off until you enable them. We do not sell Personal Data, and we do not use
        Solution Content for unrelated advertising or cross-app tracking.
      </p>

      <div className={s.title}>5) Categories of Data We Process</div>
      <p>
        Depending on your choices and device permissions, we may process: (a) account and
        sign-in data (email, provider/user IDs, display name where available) via Firebase
        Auth; (b) Image Content you capture or import, processed transiently for OCR and
        solution generation; (c) Extracted Text and Solution Content, including steps,
        formulas, and final answers; (d) technical and diagnostic data such as device model,
        OS/app version, locale, time zone, crash logs in aggregated or pseudonymized form,
        and notification tokens; (e) subscription and entitlement metadata from RevenueCat
        (product IDs, trial status, renewal outcome, expiration/cancellation dates); (f)
        analytics events (onboarding progression, camera opens, OCR success rates, solution
        generation counts, paywall views, trial start, purchase, renewal, cancellation); (g)
        optional cloud sync data if you enable Firestore. We do not receive payment card
        numbers.
      </p>

      <div className={s.title}>6) Sources of Data and Voluntary Provision</div>
      <p>
        Most data originates from you when you register, capture or upload images, request
        OCR, and generate solutions. Additional data arises automatically from App
        operation, such as performance metrics and crash diagnostics that help maintain
        stability. Enabling optional features like Firestore sync, cloud OCR, or certain
        analytics categories may cause data to be uploaded to providers associated with your
        account. Providing some data is optional, but certain features require it: without
        camera permission, capture will not function; without authentication, account-linked
        sync and purchase restoration are unavailable; without notification tokens, push
        messages cannot be delivered. You can revoke permissions in system settings, sign
        out, or disable sync at any time; doing so stops new data flows to those components
        while allowing local use within remaining capabilities.
      </p>

      <div className={s.title}>7) Camera, Photos, and Local Processing</div>
      <p>
        The App requests camera permission solely to let you photograph assignments for OCR.
        Images are processed on-device where feasible, and temporary working copies exist
        only as long as needed to recognize text and assemble a result. If you choose to
        import from the gallery, read access is used to obtain the selected photo; we do not
        scan unrelated media. By default, recognized results are stored in a local database
        (SQLite/Hive) so you can revisit recent solutions. You may delete entries in-app or
        clear the local cache. If you export to PDF or share, copies of content will be saved
        to locations you select or sent to the destination you choose, which is governed by
        that destination’s privacy practices.
      </p>

      <div className={s.title}>8) OCR Processing: On-Device and Cloud Options</div>
      <p>
        OCR can run on-device using ML Kit or, if configured and explicitly enabled, via a
        cloud OCR provider such as Cloud Vision API. On-device OCR processes Image Content
        locally and returns Extracted Text without uploading the image. Cloud OCR, when used,
        transmits the image or a derivative to the provider over HTTPS strictly to perform
        recognition and return text. We do not add marketing identifiers to OCR payloads and
        we do not retain OCR images on our own servers. If a cloud provider caches data for
        reliability or abuse prevention, retention follows that provider’s policies. You can
        choose the OCR mode in settings where offered; selecting on-device OCR maximizes
        privacy at potential cost to recognition accuracy for complex layouts.
      </p>

      <div className={s.title}>9) AI Solution Generation and Content Handling</div>
      <p>
        To produce step-by-step explanations, the App submits Extracted Text and prompt
        instructions to an AI model provider (for example, OpenAI GPT-4o-mini) over HTTPS. We
        transmit only the fields required to create the solution and enforce usage limits. We
        do not route Solution Content through our own backend for profiling. The AI provider
        may process the request transiently for output generation, safety checks, reliability,
        and fraud prevention. Solution Content is saved locally so you can review it later,
        subject to free-tier history limits. If you enable optional sync, the text of
        problems and solutions may be stored in your account’s cloud database. Avoid
        submitting sensitive personal, medical, or financial information within images or
        prompts.
      </p>

      <div className={s.title}>
        10) Subscriptions, Billing, Trials, and Entitlements
      </div>
      <p>
        Monetization uses in-app purchases via RevenueCat with a 3-day trial and plans
        currently priced at $3.99 per week or $29.99 per year. Free use is limited to up to 5
        solutions per day and restricted history. Paid access removes daily caps, unlocks
        full history, and may enable richer explanations. We receive entitlement data such as
        product identifiers, trial eligibility, renewal outcomes, expiration dates, and
        cancellation status to grant or revoke access. Payment card details are never shared
        with us. Manage or cancel subscriptions in your platform account settings. Grace
        periods, refunds, and price change notices follow platform rules and applicable law.
        We may retain pseudonymized transactional metadata for fraud prevention and
        accounting.
      </p>

      <div className={s.title}>11) History, Storage, and PDF Export</div>
      <p>
        By default, results are stored locally in a database (SQLite/Hive) with free-tier
        limits (for example, only the last two solutions remain accessible). Paid users may
        store an extended or unlimited history according to plan. You can export a solution
        as a PDF or share it through system share sheets. Exported files are saved to the
        location you select; once exported or shared, those copies are under the control of
        the chosen destination. Deleting an item in the App does not delete exported copies.
        If you enable cloud sync, solution entries and metadata may be stored in Firestore
        linked to your account, allowing access across devices after sign-in.
      </p>

      <div className={s.title}>12) Push Notifications and In-App Messages</div>
      <p>
        With your permission, the App may send notifications such as reminders (“Time to
        practice”) or service messages about subscription status or solution availability.
        Delivery requires a device token, which we and our providers use only to route
        messages. You can adjust permissions in system settings or turn off specific
        categories in-app where available. We avoid including Extracted Text or Solution
        Content in notification bodies. Some service-critical communications permitted by
        law, such as security or billing notices, may be delivered even if marketing-like
        notifications are disabled. If you uninstall the App, the platform typically
        invalidates your token, although short delays may occur before complete deactivation
        propagates.
      </p>

      <div className={s.title}>
        13) Analytics, Diagnostics, and Optional Attribution
      </div>
      <p>
        We use Firebase Analytics and crash diagnostics to understand reliability,
        performance, and feature adoption. Typical events include onboarding completion,
        camera opens, OCR success/failure rates, solution generation counts, paywall
        impressions, trial starts, purchases, renewals, cancellations, and error codes. When
        enabled, attribution may measure install sources in aggregate without attaching your
        Image Content, Extracted Text, or Solution Content. Where feasible, data is
        aggregated or pseudonymized, and retention is limited to operational windows
        necessary for trend analysis and problem resolution. You can reduce analytics using
        system privacy settings where available, understanding this may limit our ability to
        troubleshoot device-specific issues and regressions across OS versions.
      </p>

      <div className={s.title}>
        14) Legal Bases for Processing (EEA/UK) and Global Principles
      </div>
      <p>
        For users in the EEA/UK, our legal bases include: performance of a contract (core
        functionality such as OCR, solution generation, subscription access); legitimate
        interests (security, fraud prevention, product improvement) balanced against your
        rights through minimization and controls; consent (optional cloud OCR, certain
        analytics categories, notifications, and sync); and legal obligations (records,
        compliance, and responses to lawful requests). Globally, we apply similar principles
        and safeguards regardless of location, adapting to local requirements where
        applicable. We will seek additional consent if purposes materially change. We do not
        use your data for third-party behavioral advertising and we do not sell Personal
        Data.
      </p>

      <div className={s.title}>15) Data Sharing with Service Providers</div>
      <p>
        To operate the App, we share limited data with trusted providers acting on our
        instructions: Firebase Auth for sign-in, Firestore for optional sync, FCM for
        notifications, RevenueCat for entitlements and billing metadata, analytics and crash
        services for stability, OCR providers for recognition, and AI model providers for
        solution generation. Contracts require appropriate security and prohibit independent
        marketing use of your Personal Data. We may disclose information when required by
        competent authorities after validating scope and jurisdiction. In case of a business
        transition, the successor will honor this Policy or provide notice of material
        changes so you can make informed choices. We do not share Solution Content with
        social networks or ad brokers.
      </p>

      <div className={s.title}>16) Security Measures</div>
      <p>
        We apply layered administrative, technical, and organizational controls aligned with
        the App’s risk profile. Communications use HTTPS; cloud databases employ encryption
        at rest; local databases store results in app-scoped storage with options for
        encryption; access to operational tools is restricted by least privilege and logged.
        We monitor dependencies for known vulnerabilities and ship updates within reasonable
        release cycles. We segregate development and production environments and avoid using
        production Personal Data for testing. While no system is perfectly secure, we reduce
        exposure through minimization, short retention, encryption, and user controls like
        local deletion, export choice, and disabling sync. If a security incident occurs, we
        will investigate and notify as required by law.
      </p>

      <div className={s.title}>17) Data Retention, Deletion, and Backups</div>
      <p>
        We retain Personal Data only as long as necessary to deliver features, maintain
        security, comply with legal obligations, and resolve disputes. Local history remains
        until you delete entries or uninstall the App. Synced records remain in your
        account’s Firestore until you disable sync or request deletion; full purge may
        depend on provider backup cycles and replication windows. Subscription metadata can
        be retained longer for accounting and fraud prevention, typically in pseudonymized
        or aggregated form. Crash and performance logs are kept for trend analysis before
        deletion or de-identification. Verified deletion requests are processed within a
        reasonable timeframe, acknowledging that offline devices and distributed systems can
        introduce short delays.
      </p>

      <div className={s.title}>18) Your Rights and Choices</div>
      <p>
        Subject to law, you may have rights to access, rectify, delete, or port Personal
        Data; to restrict or object to certain processing; and to withdraw consent where
        processing relies on consent. In-app controls let you sign out, clear local history,
        revoke permissions, toggle notifications, and manage export preferences. Subscription
        management, including cancellation and refunds, occurs in platform account settings.
        To exercise other rights, email alexwadek@gmail.com from your sign-in address. We
        will verify your identity and respond within a reasonable time. If we cannot fulfill
        a request due to legal, security, or technical constraints, we will explain the
        reason unless prohibited.
      </p>

      <div className={s.title}>19) Children’s Privacy</div>
      <p>
        The App is not directed to children, and we do not knowingly collect Personal Data
        from users under the age where parental consent is required by applicable law. If you
        believe a minor has used the App without appropriate authorization,
        contact alexwadek@gmail.com and we will remove the information and close the account
        if applicable. Parents and guardians can use device-level controls to limit camera
        and purchase access. If we become aware that we processed data from a minor without
        proper consent, we will promptly delete such data. Any future education-specific
        features will include age-appropriate disclosures and consent mechanisms consistent
        with platform and legal requirements.
      </p>

      <div className={s.title}>
        20) Do Not Track, Identifiers, and SDK Disclosures
      </div>
      <p>
        As a native mobile app, we do not use browser cookies; instead, SDKs may store
        identifiers used to operate features like authentication, analytics, notifications,
        OCR configuration, and entitlement checks. These identifiers function similarly to
        cookies by enabling sessions and measurement. Mobile platforms provide privacy
        controls such as resetting device identifiers or limiting ad tracking; the App does
        not rely on advertising IDs for targeted ads. Browser “Do Not Track” signals
        generally do not apply to native apps. We scope SDK usage to the minimum necessary
        for each feature and avoid combining identifiers across services for unrelated
        marketing purposes.
      </p>

      <div className={s.title}>
        21) Export, Sharing, and Third-Party Destinations
      </div>
      <p>
        When you export a solution to PDF or share via system share sheets, the resulting
        file and any metadata are controlled by the destination app or service, which has its
        own privacy practices. The App does not monitor or control those environments. If you
        upload a PDF to email, cloud drives, or messaging platforms, review their terms and
        privacy policies before sharing sensitive school or personal information. Deleting a
        solution inside the App does not remove exported or shared copies. We recommend using
        device security features and cautious sharing practices, especially on shared
        devices, to prevent unintended disclosure of assignment images or explanations.
      </p>

      <div className={s.title}>22) International Data Transfers</div>
      <p>
        Depending on your location and the locations of our providers, your data may be
        processed in countries with different data protection rules. Where required, we use
        appropriate safeguards such as contractual protections and platform-level mechanisms
        for cross-border transfers. Regardless of where processing occurs, providers must
        implement security measures consistent with this Policy and applicable law. You can
        contact us for information about relevant transfer mechanisms and, where appropriate,
        summaries of contractual commitments that do not reveal confidential commercial
        information. By using the App, you understand that global processing may be necessary
        for reliable performance and support.
      </p>

      <div className={s.title}>
        23) Free Tier Limits, Abuse Prevention, and Fair Use
      </div>
      <p>
        The free tier allows up to approximately 5 solutions per day and restricts history
        access. These limitations are enforced using local counters and, where necessary,
        minimal identifiers to prevent abuse and maintain service quality. Attempts to bypass
        limits or automate excessive requests may trigger protective measures designed to
        preserve reliability for all users. Paid plans remove daily caps and unlock full
        history, subject to reasonable fair-use thresholds to control latency and costs. We
        will communicate material changes to limits according to platform rules and
        applicable law and will not use Extracted Text or Solution Content to build
        advertising profiles.
      </p>

      <div className={s.title}>
        24) Accuracy, Limitations, and Educational Use
      </div>
      <p>
        AI-generated solutions aim to be helpful and educational, but perfection is not
        guaranteed. The App is intended as a study aid, not a substitute for your own work or
        professional academic advice. Before submitting schoolwork, review explanations for
        accuracy and adapt them to your assignment’s instructions and permitted collaboration
        rules. We do not verify the correctness of every solution or ensure it matches the
        grading rubric of any institution. By using the App, you accept responsibility for
        how outputs are used. This section concerns product expectations and does not limit
        your statutory privacy rights elsewhere in this Policy.
      </p>

      <div className={s.title}>25) Changes to This Policy</div>
      <p>
        We may update this Policy to reflect operational, legal, or regulatory developments.
        The “Last Updated” date will change when updates occur. Material changes that affect
        your rights or our processing of Personal Data will be highlighted in-app, and where
        required we will request consent for new uses. Continued use of the App after changes
        take effect signifies acceptance. We encourage periodic review, especially when
        enabling new features such as cloud OCR, expanded export options, or additional sync
        scopes. If you disagree with updates, you may stop using the App and request account
        deletion and removal of synced data, subject to verification and applicable retention
        obligations.
      </p>

      <div className={s.title}>
        26) How to Submit Privacy Requests and Verification Steps
      </div>
      <p>
        To exercise your rights, email alexwadek@gmail.com from the address used to sign in.
        Specify the request type (access, deletion, correction, restriction, objection,
        portability) and provide context such as device model and app version. We verify
        identity to prevent unauthorized access or deletion, and may request limited
        additional information or a confirmation code. If you appoint an authorized agent, we
        may require proof of authorization and your verification. Responses are provided
        within a reasonable time. Where feasible, data is supplied in a portable,
        machine-readable format. We may decline or limit requests when permitted by law, for
        example if fulfilling the request would compromise security or the privacy of others.
      </p>
    </PolicyLayout>
  );
}
